import React, { useEffect, useMemo, useState } from "react";
import UserAvatar from "../../../common/UserAvatar/UserAvatar";
import { executeQuery } from "../../../../graphql/client";
import "./HostPicker.css";

// Admin-only host picker for CreateEventAdmin + EditEventsTab.
// Replaces the legacy hardcoded host1 placeholder so events actually
// get attributed and the captain-stats matcher
// (publicCaptains.eventsHostedCount / recentlyHostedEvents) can light
// up.
//
// Source of truth: the publicCaptains graphql query — the same
// admin-curated showcase users see on the dashboard. The picker
// stores host displayNames; the captain-stats matcher does
// case-insensitive matching against displayName OR linkedEmail, so
// either works, but displayName reads better in the raw DDB row.
// We deliberately don't select linkedEmail here — it's not on the
// public Captain type (privacy-by-default).
//
// First tile is always "Me (<email>)" so an admin can attribute the
// event to themselves without first having to create their own
// captain showcase row. The string we store for Me is the admin's
// email — so as soon as they DO create a captain row with
// linkedEmail set to their email, historical events light up.

const PUBLIC_CAPTAINS_QUERY = `
  query PublicCaptainsForPicker {
    publicCaptains {
      id
      displayName
      profileImgKey
    }
  }
`;

const ME_KEY = "__me__";

const HostPicker = ({ adminEmail, value, onChange }) => {
  const [captains, setCaptains] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await executeQuery(PUBLIC_CAPTAINS_QUERY, {});
        if (cancelled) return;
        setCaptains(result?.publicCaptains || []);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Could not load captains");
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(
    () => new Set(Array.isArray(value) ? value : []),
    [value]
  );

  const toggle = (host) => {
    const next = new Set(selected);
    if (next.has(host)) next.delete(host);
    else next.add(host);
    onChange(Array.from(next));
  };

  const meTile = useMemo(() => {
    if (!adminEmail) return null;
    return {
      key: ME_KEY,
      displayName: `Me (${adminEmail})`,
      // The string we actually store on the event. Picking this tile
      // adds the admin's lowercase email so it's matchable later when
      // a captain row is created with linkedEmail = adminEmail.
      hostString: adminEmail.toLowerCase(),
      profileImgKey: null,
    };
  }, [adminEmail]);

  const renderTile = (tile, isSelected, onPress) => (
    <button
      type="button"
      key={tile.key}
      className={`host-picker__tile${
        isSelected ? " host-picker__tile--selected" : ""
      }`}
      onClick={onPress}
      aria-pressed={isSelected}
    >
      <span className="host-picker__avatar-wrap">
        <UserAvatar
          name={tile.displayName}
          imgKey={tile.profileImgKey}
          size={56}
        />
        {isSelected && (
          <span className="host-picker__check" aria-hidden="true">
            ✓
          </span>
        )}
      </span>
      <span className="host-picker__name">{tile.displayName}</span>
    </button>
  );

  return (
    <div className="host-picker">
      <div className="host-picker__label">Hosts</div>
      <p className="host-picker__hint">
        Tap to credit one or more captains. Powers their events-hosted count.
      </p>
      {!loaded && <div className="host-picker__info">Loading captains…</div>}
      {loadError && (
        <div className="host-picker__error">
          Couldn't load captains: {loadError}. You can still create the event.
        </div>
      )}
      <div className="host-picker__strip">
        {meTile &&
          renderTile(meTile, selected.has(meTile.hostString), () =>
            toggle(meTile.hostString)
          )}
        {captains.map((c) =>
          renderTile(
            { key: c.id, displayName: c.displayName, profileImgKey: c.profileImgKey },
            selected.has(c.displayName),
            () => toggle(c.displayName)
          )
        )}
        {loaded && !meTile && captains.length === 0 && (
          <div className="host-picker__empty">
            No captains in the showcase yet. Add some in the Captains admin
            tab — until then, this event will save with no host attribution.
          </div>
        )}
      </div>
    </div>
  );
};

export default HostPicker;
